import { createReference, MedplumClient, resolveId } from "@medplum/core";
import { Condition, Observation, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";

import { formatISO, parseISO, startOfDay } from "date-fns";

import { LOINCCodes } from "@/fhir/enums/loinc";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { fetchPregnancyConditionForPregnancyRecord } from "@/pregnancy/services/condition";
import { fetchEDDForPregnancyRecord } from "@/pregnancy/services/edd";
import {
  fetchLastMenstrualPeriodForPregnancyRecord,
  fetchLMPReliabilityForPregnancyRecord,
} from "@/pregnancy/services/lmp";
import { calculateEDDFromLastMenstrualPeriod } from "@/pregnancy/utils/edd";
import { setAccounts } from "@/utils/accounts";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import {
  getQuestionnaireResponseFromFormValues,
  QuestionnaireFormValues,
} from "@/utils/questionnaires";

import { EddAnswersFormType } from "../zod-schemas/edd-form";

/** EDD-specific observations only. Pregnancy status is a separate concern. */
export interface EDDAnswers {
  eddDisplay: Observation | undefined;
  lmp: Observation | undefined;
  lmpEdd: Observation | undefined;
  lmpCertain: Observation | undefined;
  ultrasoundDate: Observation | undefined;
  usEdd: Observation | undefined;
  customEdd: Observation | undefined;
  gaComposite: Observation | undefined;
}

interface PregnancyRecordEDDResult {
  eddObservations: EDDAnswers;
  pregnancyCondition: Condition | undefined;
}

export async function fetchEDDQuestionnaire(medplum: MedplumClient): Promise<Questionnaire> {
  const response = await medplum.searchOne("Questionnaire", {
    url: Questionnaires.EDD,
    _sort: "-_lastUpdated",
  });
  if (!response) {
    showSystemErrorNotification();
    throw new Error("EDD Questionnaire not found");
  }
  return response;
}

export async function fetchEDDQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  clientId: string,
): Promise<QuestionnaireResponse | undefined> {
  const response = await medplum.searchOne("QuestionnaireResponse", {
    subject: joinReferenceString("Patient", clientId),
    questionnaire: createReference(questionnaire).reference,
    _count: 1,
    _sort: "-_lastUpdated",
  });
  return response;
}

export async function createEDDQuestionnaireResponse(
  medplum: MedplumClient,
  questionnaire: Questionnaire,
  clientId: string,
  values: QuestionnaireFormValues,
): Promise<QuestionnaireResponse> {
  const profile = medplum.getProfile()!;

  if (!profile) {
    showSystemErrorNotification();
    throw new Error("Profile not found");
  }

  const subject = joinReferenceString("Patient", clientId);
  const questionnaireResponse = getQuestionnaireResponseFromFormValues(
    questionnaire,
    values,
    {
      reference: subject,
    },
    {
      reference: joinReferenceString("Practitioner", profile.id!),
    },
  );

  const newQuestionnaireResponse = await medplum.createResource(questionnaireResponse);
  await setAccounts(medplum, newQuestionnaireResponse, [], { extractFromPatient: true });
  return newQuestionnaireResponse;
}

export const INITIAL_VALUES: EddAnswersFormType = {
  eddDisplay: undefined,
  isPregnant: undefined,
  lmpDate: undefined,
  lmpCertain: undefined,
  lmpEddDate: undefined,
  ultrasoundDate: undefined,
  ultrasoundGestationalAgeWeeks: undefined,
  ultrasoundGestationalAgeDays: undefined,
  ultrasoundEddDate: undefined,
  customEddDate: undefined,
};

/**
 * Builds initial form values for the EDD form by merging observation data with
 * saved questionnaire response answers.
 *
 * When `pregnancyRecordId` is provided, observations are fetched scoped to that
 * `PregnancyRecord` (`List`) resource via `_has:List:item:_id` queries. Otherwise
 * falls back to a loose per-patient observation search.
 *
 * `isPregnant` is derived from the linked pregnancy `Condition` (new path), or the
 * loose pregnancy-status `Observation` (fallback/old path), then overridden by any
 * value saved in the questionnaire response.
 *
 * @param medplum - Medplum client
 * @param subject - Patient reference string (e.g. `"Patient/123"`)
 * @param questionnaireResponse - Most recent saved EDD questionnaire response
 * @param pregnancyRecordId - Optional `List` resource ID for the active pregnancy record
 * @returns Merged form values ready for use as initial state
 */
export async function createEDDInitialValues(
  medplum: MedplumClient,
  questionnaireResponse: QuestionnaireResponse | undefined,
  pregnancyRecordId?: string,
) {
  // No active pregnancy record (e.g. after close-out, when the List is retired and its
  // EDD Observations are cancelled) means there is nothing to show. Return a reset form
  // and ignore the un-scoped EDD QuestionnaireResponse, which is still the newest match
  // for the patient and would otherwise re-populate stale dates from the closed pregnancy.
  if (!pregnancyRecordId) {
    return { ...INITIAL_VALUES, isPregnant: false };
  }

  const responseValues = convertQuestionnaireResponseToFormValues(questionnaireResponse);

  const { eddObservations, pregnancyCondition } = await fetchEDDObservationsForPregnancyRecord(
    medplum,
    pregnancyRecordId,
  );
  const isPregnant = pregnancyCondition?.clinicalStatus?.coding?.[0]?.code === "active";

  const obsValues = convertEddObservationsToFormValues(eddObservations);

  // isPregnant is placed before responseValues so a saved QR answer can still override it.
  const merged = { ...INITIAL_VALUES, ...obsValues, isPregnant, ...responseValues };
  if (merged.isPregnant === false) {
    return { ...INITIAL_VALUES, isPregnant: false };
  }

  // The EDD QR captures what the user entered in the EDD drawer. When LMP is
  // later updated via the intake form, only the LMP Observation is written —
  // the EDD QR is not touched. Re-apply observation LMP fields when the LMP
  // Observation is newer than the QR so stale drawer values don't persist.
  const lmpObsLastUpdated = eddObservations.lmp?.meta?.lastUpdated;
  const qrLastUpdated = questionnaireResponse?.meta?.lastUpdated;
  const lmpObsIsNewer =
    lmpObsLastUpdated !== undefined &&
    (qrLastUpdated === undefined || new Date(lmpObsLastUpdated) > new Date(qrLastUpdated));

  if (lmpObsIsNewer) {
    merged.lmpDate = obsValues.lmpDate;
    merged.lmpEddDate = obsValues.lmpEddDate;
    merged.lmpCertain = obsValues.lmpCertain;
  }

  return merged;
}

/**
 * Fetches EDD-related observations scoped to a specific pregnancy record (`List`
 * resource), using `_has:List:item:_id` queries rather than loose per-patient searches.
 *
 * Runs all root fetches in parallel, then resolves the EDD wrapper's `hasMember`
 * references in a single `_id` batch search and categorises each member by LOINC code.
 *
 * Returns the linked `Condition` directly so the caller can derive `isPregnant` from
 * `clinicalStatus`.
 *
 * @param medplum - Medplum client
 * @param pregnancyRecordId - FHIR `List` resource ID representing the pregnancy record
 * @returns `PregnancyRecordEDDResult` containing EDD observations and the linked `Condition`
 */
export async function fetchEDDObservationsForPregnancyRecord(
  medplum: MedplumClient,
  pregnancyRecordId: string,
): Promise<PregnancyRecordEDDResult> {
  const [eddWrapper, lmp, lmpCertain, pregnancyCondition] = await Promise.all([
    fetchEDDForPregnancyRecord(medplum, pregnancyRecordId),
    fetchLastMenstrualPeriodForPregnancyRecord(medplum, pregnancyRecordId),
    fetchLMPReliabilityForPregnancyRecord(medplum, pregnancyRecordId),
    fetchPregnancyConditionForPregnancyRecord(medplum, pregnancyRecordId),
  ]);

  const base: EDDAnswers = {
    eddDisplay: undefined,
    lmp,
    lmpEdd: undefined,
    lmpCertain,
    ultrasoundDate: undefined,
    usEdd: undefined,
    customEdd: undefined,
    gaComposite: undefined,
  };

  if (!eddWrapper?.hasMember?.length) {
    return { eddObservations: base, pregnancyCondition };
  }

  const memberIds = eddWrapper.hasMember
    .map((member) => resolveId(member))
    .filter(Boolean) as string[];

  if (!memberIds.length) {
    return { eddObservations: base, pregnancyCondition };
  }

  const { bundle } = await medplum.searchResources("Observation", {
    _id: memberIds.join(","),
    _count: memberIds.length,
  });

  const members = getResourcesFromBundle("Observation", bundle) ?? [];

  const byCode = (code: string) =>
    members.find((observation) => observation.code?.coding?.some((coding) => coding.code === code));

  return {
    eddObservations: {
      ...base,
      eddDisplay: members.find((observation) =>
        observation.code?.coding?.some((coding) => coding.code === QuiltedHealthCodes.EDDDisplay),
      ),
      lmpEdd: byCode(LOINCCodes.DeliveryDateEstimatedFromLMP),
      ultrasoundDate: byCode(LOINCCodes.UltrasoundDate),
      usEdd: byCode(LOINCCodes.DeliveryDateEstimatedFromUS),
      customEdd: byCode(LOINCCodes.DeliveryDateEstimated),
      gaComposite: byCode(LOINCCodes.GACompositeFromUS),
    },
    pregnancyCondition,
  };
}

function convertQuestionnaireResponseToFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): EddAnswersFormType | undefined {
  if (!questionnaireResponse?.item?.[0]?.item) {
    return undefined;
  }
  const values: Record<string, unknown> = {};
  for (const item of questionnaireResponse.item[0].item) {
    if (item?.answer?.[0]) {
      const answer = item.answer[0];
      const linkId = item.linkId as keyof EddAnswersFormType;
      if (answer.valueCoding) {
        values[linkId] = answer.valueCoding;
      } else if (answer.valueDate) {
        values[linkId] = parseISO(answer.valueDate);
      } else if (answer.valueString) {
        values[linkId] = answer.valueString;
      } else if (answer.valueInteger !== undefined) {
        values[linkId] = answer.valueInteger;
      } else if (answer.valueBoolean !== undefined) {
        values[linkId] = answer.valueBoolean;
      }
    }
  }
  return values as EddAnswersFormType;
}

function getGAFormValuesFromObservation(observation: Observation | undefined) {
  const observationValue = observation?.valueInteger;
  if (!observationValue) {
    return {
      ultrasoundGestationalAgeDays: undefined,
      ultrasoundGestationalAgeWeeks: undefined,
    };
  }
  return {
    ultrasoundGestationalAgeDays: observationValue % 7,
    ultrasoundGestationalAgeWeeks: Math.floor(observationValue / 7),
  };
}

/**
 * Maps EDD observations to form field values. Does not set `isPregnant` — that is
 * derived separately by `createEDDInitialValues` from the pregnancy `Condition`
 * or status `Observation`, then merged in after this function returns.
 */
function convertEddObservationsToFormValues(
  eddObservations: EDDAnswers,
): Omit<EddAnswersFormType, "isPregnant"> {
  const { ultrasoundGestationalAgeDays, ultrasoundGestationalAgeWeeks } =
    getGAFormValuesFromObservation(eddObservations.gaComposite);

  const parseDateFromValueDateTime = (valueDateTime: string | undefined): Date | undefined => {
    if (!valueDateTime) return undefined;
    return startOfDay(parseISO(valueDateTime));
  };

  const { eventDate: lmpDate, eddDate: lmpEddDate } = pairedEventAndEddFromObservations(
    eddObservations.lmp,
    eddObservations.lmpEdd,
    parseDateFromValueDateTime,
  );

  const { eventDate: ultrasoundDate, eddDate: ultrasoundEddDate } =
    pairedEventAndEddFromObservations(
      eddObservations.ultrasoundDate,
      eddObservations.usEdd,
      parseDateFromValueDateTime,
    );

  let customEddDate = undefined;
  if (isFinalEDDObservationStatus(eddObservations.customEdd)) {
    customEddDate = parseDateFromValueDateTime(eddObservations.customEdd?.valueDateTime);
  }

  return {
    eddDisplay: eddObservations.eddDisplay?.valueCodeableConcept?.coding?.[0],
    lmpDate,
    lmpEddDate,
    lmpCertain: eddObservations.lmpCertain?.valueString,
    ultrasoundDate,
    ultrasoundEddDate,
    ultrasoundGestationalAgeDays,
    ultrasoundGestationalAgeWeeks,
    customEddDate,
  };
}

function isFinalEDDObservationStatus(eddObservation: Observation | undefined): boolean {
  return eddObservation?.status === "final";
}

function pairedEventAndEddFromObservations(
  eventObservation: Observation | undefined,
  eddObservation: Observation | undefined,
  parseValueDateTime: (v: string | undefined) => Date | undefined,
): { eventDate: Date | undefined; eddDate: Date | undefined } {
  if (!isFinalEDDObservationStatus(eventObservation)) {
    return { eventDate: undefined, eddDate: undefined };
  }
  const eventDate = parseValueDateTime(eventObservation?.valueDateTime);
  let eddDate: Date | undefined;
  if (eddObservation?.valueDateTime) {
    eddDate = parseValueDateTime(eddObservation.valueDateTime);
  } else if (eventDate) {
    eddDate = parseISO(
      calculateEDDFromLastMenstrualPeriod(
        formatISO(startOfDay(eventDate), { representation: "date" }),
      ),
    );
  }

  return { eventDate, eddDate };
}
